/**
 * A Worker's live run log, and whether its interactive terminal is shown at all.
 *
 * Owner decision 2026-10-04: a Worker's turn runs as a process, not as text typed into a
 * terminal, so what the owner watches is that process's output - a read-only log - and the
 * terminal is a debug view that is off unless this browser turns it on.
 *
 * One thing shows the terminal without that switch (#1133): the Worker's own terminal asking
 * the owner something. Only the owner can answer it, and only there.
 *
 * The log is built only from recorded run_progress rows, which the node writes for one
 * assignment while its run is still going. Each row is the newest part of the output, so
 * consecutive rows overlap; joining them on that overlap gives one continuous log for as long
 * as this page has been watching. Pure: nothing here reads, writes or subscribes.
 */
import { departmentLabel } from '@brunel/shared';
import type { RunProgress, RunningTask } from './data';

const KEY = 'brunel.command.worker.terminal-debug';

interface StringStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Was the debug terminal turned on in this browser? Anything else, or no store, means off. */
export function recallWorkerTerminalDebug(store: StringStore | null): boolean {
  if (store === null) return false;
  try {
    return store.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

/** Remember it. A browser with storage turned off simply does not. */
export function rememberWorkerTerminalDebug(store: StringStore | null, shown: boolean): void {
  if (store === null) return;
  try {
    store.setItem(KEY, shown ? '1' : '0');
  } catch {
    /* nothing to say to the owner about this */
  }
}

export type WorkerSurfaceView = 'log' | 'terminal' | 'activity';

/**
 * Whether a Worker surface has the interactive terminal on it.
 *
 * In debug, always. Otherwise only for a question: while the surface is open and the
 * Worker's own terminal is waiting on the owner, and after that for as long as the owner
 * stays on the terminal. The first key of an answer already ends the wait, so the terminal
 * must not go away under an answer that is still being typed; it goes when the owner leaves it.
 */
export function workerTerminalShown(input: {
  /** "Show terminals (debug)" is on in this browser. */
  readonly terminalDebug: boolean;
  /** The Worker's own terminal is asking the owner something. */
  readonly terminalWaiting: boolean;
  /** The surface is open, not collapsed. */
  readonly active: boolean;
  /** The view the surface is on. */
  readonly view: WorkerSurfaceView;
}): boolean {
  return input.terminalDebug || input.view === 'terminal' || (input.active && input.terminalWaiting);
}

/** The Worker surface's views, in order. The terminal is one of them only while it is shown. */
export function workerSurfaceViews(terminalShown: boolean): readonly WorkerSurfaceView[] {
  return terminalShown ? ['log', 'terminal', 'activity'] : ['log', 'activity'];
}

/** The view a Worker surface opens on: its terminal when that is where the owner is needed. */
export function workerSurfaceOpeningView(terminalWaiting: boolean): WorkerSurfaceView {
  return terminalWaiting ? 'terminal' : 'log';
}

export const WORKER_SURFACE_VIEW_LABEL: Readonly<Record<WorkerSurfaceView, string>> = {
  log: 'Run log',
  terminal: 'Terminal (debug)',
  activity: 'Activity',
};

/** What the terminal view is called, which says why it is there. */
export function workerTerminalViewLabel(input: {
  readonly terminalDebug: boolean;
  readonly terminalWaiting: boolean;
}): string {
  if (input.terminalWaiting) return 'Terminal · waiting for you';
  return input.terminalDebug ? WORKER_SURFACE_VIEW_LABEL.terminal : 'Terminal';
}

/** The most log one Worker surface keeps, in characters. The newest is kept. */
export const RUN_LOG_KEEP = 200_000;
/** What sits between two recorded pieces that do not join. */
export const RUN_LOG_GAP = '…';
/**
 * The shortest stretch two pieces must share to be joined on it. Consecutive rows of one run
 * share nearly all of their text; a few characters in common is coincidence, and joining on
 * coincidence would quietly drop them.
 */
const MIN_OVERLAP = 8;

const ESCAPES = /\u001b\[[0-?]*[ -/]*[@-~]|\u001b\][^\u0007\u001b]*(?:\u0007|\u001b\\)|\u001b[@-_]/g;
const CONTROLS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;

/** A recorded tail as text to read: no escape sequences, and indentation left as it was. */
export function runLogText(tail: string): string {
  return tail.replace(ESCAPES, '').replace(/\r\n?/g, '\n').replace(CONTROLS, '').replace(/[ \t]+$/gm, '');
}

/**
 * The log so far with a newer recorded piece added. Where the end of the log is the start of
 * the piece, only what is new is added. Where they do not meet - output went by between two
 * rows, or the tool replaced its line - the piece is added whole after a gap line, so nothing
 * recorded is ever dropped and nothing is ever shown as continuous that was not.
 */
export function joinRunLog(log: string, piece: string): string {
  if (piece === '') return log;
  if (log === '') return piece;
  if (log.endsWith(piece)) return log;
  // The longest stretch that is both the end of the log and the start of the piece.
  const first = piece.charCodeAt(0);
  for (let overlap = Math.min(log.length, piece.length - 1); overlap >= MIN_OVERLAP; overlap -= 1) {
    const at = log.length - overlap;
    if (log.charCodeAt(at) === first && piece.startsWith(log.slice(at))) return log + piece.slice(overlap);
  }
  return `${log}\n${RUN_LOG_GAP}\n${piece}`;
}

export interface WorkerRunLog {
  /** The run this log belongs to. A different run starts a different log. */
  readonly assignmentId: string;
  readonly title: string;
  /** The department this run is working as, by its name. Null when its task names none. */
  readonly department: string | null;
  /** The newest recorded row that is in the log. Zero before the first one. */
  readonly seq: number;
  readonly text: string;
  /** When the newest row in the log was recorded. */
  readonly observedAt: string | null;
  /** False once the run is no longer among the running ones: the log is its last. */
  readonly running: boolean;
}

/**
 * The log after one more read of the room.
 *
 * `run` is the Worker's run in progress and `progress` its newest recorded row, both exactly
 * as the room read them. A row for another assignment is never added to this log. When the
 * run has ended the log is kept, marked as ended, until this Worker starts another.
 */
export function nextWorkerRunLog(
  current: WorkerRunLog | null,
  run: RunningTask | null,
  progress: RunProgress | null,
): WorkerRunLog | null {
  if (run === null) return current === null || !current.running ? current : { ...current, running: false };
  const kept = current !== null && current.assignmentId === run.assignment_id ? current : null;
  // The department is the run's own. A row read without the column says nothing about it, so
  // what this log already knew stands; a different run starts from nothing.
  const department = run.department_key === undefined ? kept?.department ?? null : departmentLabel(run.department_key);
  // The same object when nothing changed, so a read that brought nothing new redraws nothing.
  const base: WorkerRunLog = kept !== null
    ? (kept.running && kept.title === run.title && kept.department === department
      ? kept : { ...kept, title: run.title, department, running: true })
    : { assignmentId: run.assignment_id, title: run.title, department, seq: 0, text: '', observedAt: null, running: true };
  if (progress === null || progress.assignment_id !== run.assignment_id || progress.seq <= base.seq) return base;
  const joined = joinRunLog(base.text, runLogText(progress.tail).trim());
  // Cut on a line, so the oldest line shown is a whole one.
  const text = joined.length <= RUN_LOG_KEEP
    ? joined
    : joined.slice(joined.indexOf('\n', joined.length - RUN_LOG_KEEP) + 1);
  return { ...base, seq: progress.seq, text, observedAt: progress.observed_at };
}

/** One line saying whose log this is: the Worker, the department it is working as, the task, and the run's own assignment. */
export function workerRunLogHeading(
  worker: { readonly number: number; readonly name: string },
  log: WorkerRunLog | null,
): string {
  const who = `Worker ${worker.number} ${worker.name}`;
  if (log === null) return `${who} · no run in progress`;
  return `${who}${log.department === null ? '' : ` · ${log.department} department`} · ${log.title} · assignment ${log.assignmentId.slice(0, 8)}${log.running ? '' : ' · run ended'}`;
}
